/**
 * Sprint 2 (S2.E) — Centralised plan-change broadcaster.
 *
 * The subscription tab handles the upgrade/portal/instant flows but several
 * other surfaces (`ApplicationsPage`, `JobPositionsPage`, dashboard widgets)
 * carry derived state that depends on the plan: PENDING_UPGRADE counters,
 * positions cap pre-check, AI 402 toasts, etc. Hand-rolling
 * `invalidateQueries` in every consumer scattered logic and missed
 * non-React-Query state (e.g. local `useState` arrays).
 *
 * Pattern: a single typed event fires after every plan change. Consumers
 * subscribe with `onPlanChanged()` (returns an unsubscribe). React-Query
 * caches are invalidated centrally; non-RQ state hooks just call their
 * own refetch helper from inside the listener.
 *
 * The event piggybacks on `window` so background scripts and devtools can
 * observe it. We intentionally don't carry the new plan id in the payload —
 * consumers should re-read it from `useBillingStatus` to avoid the FE
 * duplicating the resolver logic.
 */

const EVENT_NAME = 'hiromatch:plan-changed';

export interface PlanChangedDetail {
  /** Where the change originated, for debug logs. Optional. */
  source?: 'checkout-success' | 'portal-return' | 'instant-checkout' | 'agency-checkout' | 'manual';
  /** Plan id we expect the FE to settle on. Best-effort hint, not the source of truth. */
  planId?: string;
}

export function emitPlanChanged(detail: PlanChangedDetail = {}): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<PlanChangedDetail>(EVENT_NAME, { detail }));
}

export function onPlanChanged(handler: (detail: PlanChangedDetail) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const wrapped = (e: Event) => handler((e as CustomEvent<PlanChangedDetail>).detail ?? {});
  window.addEventListener(EVENT_NAME, wrapped);
  return () => window.removeEventListener(EVENT_NAME, wrapped);
}

const USAGE_EVENT_NAME = 'hiromatch:billing-usage-changed';

/** Fire after AI chat/autofill or bulk process consumes billing pools. */
export function emitBillingUsageChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(USAGE_EVENT_NAME));
}

export function onBillingUsageChanged(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(USAGE_EVENT_NAME, handler);
  return () => window.removeEventListener(USAGE_EVENT_NAME, handler);
}
